/**
 * AddContextItemDialog - Dialog for adding context items to a chatbot session
 */
import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CONTEXT_ITEM_TYPES } from '@/types/chatbot'
import { ContextItemTypeIcon, getTypeLabel } from './ContextItemTypeIcon'

export interface AddContextItemDialogProps {
  /** Whether the dialog is open */
  open: boolean
  /** Callback when dialog is closed */
  onOpenChange: (open: boolean) => void
  /** Callback when a context item should be added */
  onAdd: (type: string, value: string) => Promise<void>
  /** Whether adding is in progress */
  isLoading?: boolean
}

const typeOptions = [
  { type: CONTEXT_ITEM_TYPES.ADO_WORKITEM, placeholder: 'Work item ID (e.g., 12345)' },
  { type: CONTEXT_ITEM_TYPES.ADO_PR, placeholder: 'Pull request ID (e.g., 789)' },
  { type: CONTEXT_ITEM_TYPES.ADO_REPO, placeholder: 'Repository name' },
  { type: CONTEXT_ITEM_TYPES.ADO_BRANCH, placeholder: 'Branch name (e.g., feature/auth)' },
  { type: CONTEXT_ITEM_TYPES.WIKI_PAGE, placeholder: 'Wiki page path (e.g., /docs/setup)' },
  { type: CONTEXT_ITEM_TYPES.FILE, placeholder: 'File path (e.g., src/app.ts)' },
  { type: CONTEXT_ITEM_TYPES.URL, placeholder: 'URL (e.g., https://docs.example.com)' },
  { type: CONTEXT_ITEM_TYPES.NOTE, placeholder: 'Note text' },
]

export function AddContextItemDialog({
  open,
  onOpenChange,
  onAdd,
  isLoading = false,
}: AddContextItemDialogProps) {
  const [selectedType, setSelectedType] = useState<string>(CONTEXT_ITEM_TYPES.ADO_WORKITEM)
  const [value, setValue] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const currentOption = typeOptions.find((opt) => opt.type === selectedType) || typeOptions[0]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (value.trim() && !isSubmitting) {
      setIsSubmitting(true)
      try {
        await onAdd(selectedType, value.trim())
        // Reset form after successful submit
        setValue('')
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && !isSubmitting) {
      // Reset form when closing
      setValue('')
      setSelectedType(CONTEXT_ITEM_TYPES.ADO_WORKITEM)
    }
    onOpenChange(newOpen)
  }

  const effectiveLoading = isLoading || isSubmitting

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Add Context Item</DialogTitle>
            <DialogDescription>
              Add context to help the assistant understand what you&apos;re working on.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="type">Type</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {typeOptions.map((option) => (
                    <SelectItem key={option.type} value={option.type}>
                      <div className="flex items-center gap-2">
                        <ContextItemTypeIcon type={option.type} size="sm" />
                        <span>{getTypeLabel(option.type)}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="value">Value</Label>
              <Input
                id="value"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={currentOption.placeholder}
                disabled={effectiveLoading}
                autoFocus
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={effectiveLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!value.trim() || effectiveLoading}>
              {effectiveLoading ? 'Adding...' : 'Add'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
